import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
    Haptics,
    ImpactStyle,
    NotificationType,
} from "@capacitor/haptics";
import { useAccountPreferences } from "@/queries/account";
import { isNativeApp } from "@/lib/runtime";

const HapticsContext = createContext({
    enabled: true,
    intensity: "normal",
    isSupported: false,
    setEnabled: () => { },
    setIntensity: () => { },
    tap: () => { },
    selection: () => { },
    light: () => { },
    medium: () => { },
    heavy: () => { },
    success: () => { },
    warning: () => { },
    error: () => { },
    custom: (_pattern) => { },
    previewIntensity: (_intensity) => { },
});

function supportsWebVibration() {
    try { return typeof navigator !== "undefined" && "vibrate" in navigator; }
    catch { return false; }
}

const WEB_PATTERNS = {
    tap: 10,
    selection: [8, 40, 8],
    light: [10],
    medium: [20],
    heavy: [35],
    success: [8, 40, 16],
    warning: [6, 30, 6, 30, 12],
    error: [20, 40, 20],
};

function normalizeIntensity(value) {
    return ["weak", "normal", "strong"].includes(value) ? value : "normal";
}

function scaleWebPattern(pattern, intensity) {
    const factor = intensity === "weak" ? 0.6 : intensity === "strong" ? 1.4 : 1;
    const scale = (duration) => Math.max(1, Math.round(Number(duration || 0) * factor));
    return Array.isArray(pattern) ? pattern.map(scale) : scale(pattern);
}

function customDuration(pattern) {
    if (Array.isArray(pattern)) {
        return Math.max(1, pattern.reduce((total, duration) => total + Number(duration || 0), 0));
    }
    return Math.max(1, Number(pattern) || 1);
}

function impactStyleForIntensity(intensity) {
    if (intensity === "weak") return ImpactStyle.Light;
    if (intensity === "strong") return ImpactStyle.Heavy;
    return ImpactStyle.Medium;
}

export function HapticsProvider({ children, defaultEnabled = true }) {
    const [enabled, setEnabled] = useState(defaultEnabled);
    const [intensity, setIntensity] = useState("normal");
    const [isSupported, setIsSupported] = useState(false);
    const { data } = useAccountPreferences();
    const native = isNativeApp();

    useEffect(() => {
        if (!data?.preferences) return;
        setEnabled(data.preferences.allow_haptics !== false);
        setIntensity(normalizeIntensity(data.preferences.haptic_intensity));
    }, [data]);

    useEffect(() => {
        const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
        setIsSupported(!reduceMotion && (native || supportsWebVibration()));
    }, [native]);

    const run = useCallback((nativeAction, webPattern) => {
        if (!enabled || !isSupported) return;

        if (native) {
            Promise.resolve(nativeAction()).catch(() => { });
            return;
        }

        try { navigator.vibrate(webPattern); } catch { /* Unsupported web runtime. */ }
    }, [enabled, isSupported, native]);

    const scaledImpact = useCallback((baseStyle, webPattern) => {
        const styleOrder = [ImpactStyle.Light, ImpactStyle.Medium, ImpactStyle.Heavy];
        const baseIndex = styleOrder.indexOf(baseStyle);
        const shift = intensity === "weak" ? -1 : intensity === "strong" ? 1 : 0;
        const style = styleOrder[Math.max(0, Math.min(styleOrder.length - 1, baseIndex + shift))];
        return run(
            () => Haptics.impact({ style }),
            scaleWebPattern(webPattern, intensity),
        );
    }, [intensity, run]);

    const scaledNotification = useCallback((type, webPattern) => {
        const nativeAction = intensity === "normal"
            ? () => Haptics.notification({ type })
            : () => Haptics.impact({ style: impactStyleForIntensity(intensity) });
        return run(nativeAction, scaleWebPattern(webPattern, intensity));
    }, [intensity, run]);

    const previewIntensity = useCallback((nextIntensity) => {
        const normalized = normalizeIntensity(nextIntensity);
        const style = normalized === "weak"
            ? ImpactStyle.Light
            : normalized === "strong"
                ? ImpactStyle.Heavy
                : ImpactStyle.Medium;
        return run(
            () => Haptics.impact({ style }),
            scaleWebPattern(WEB_PATTERNS.light, normalized),
        );
    }, [run]);

    const api = useMemo(() => ({
        enabled,
        intensity,
        isSupported,
        setEnabled,
        setIntensity,
        tap: () => run(
            () => Haptics.impact({ style: impactStyleForIntensity(intensity) }),
            scaleWebPattern(WEB_PATTERNS.tap, intensity),
        ),
        selection: () => run(
            () => Haptics.impact({ style: impactStyleForIntensity(intensity) }),
            scaleWebPattern(WEB_PATTERNS.selection, intensity),
        ),
        light: () => scaledImpact(ImpactStyle.Light, WEB_PATTERNS.light),
        medium: () => scaledImpact(ImpactStyle.Medium, WEB_PATTERNS.medium),
        heavy: () => scaledImpact(ImpactStyle.Heavy, WEB_PATTERNS.heavy),
        success: () => scaledNotification(NotificationType.Success, WEB_PATTERNS.success),
        warning: () => scaledNotification(NotificationType.Warning, WEB_PATTERNS.warning),
        error: () => scaledNotification(NotificationType.Error, WEB_PATTERNS.error),
        custom: (pattern) => run(
            () => Haptics.vibrate({ duration: customDuration(scaleWebPattern(pattern, intensity)) }),
            scaleWebPattern(pattern, intensity),
        ),
        previewIntensity,
    }), [enabled, intensity, isSupported, previewIntensity, run, scaledImpact, scaledNotification]);

    return (
        <HapticsContext.Provider value={api}>
            {children}
        </HapticsContext.Provider>
    );
}

export function useHaptics() {
    return useContext(HapticsContext);
}
